import { getJourneyTotalFareForFareClass, hasSufficientSeats } from './utils'
import { useEffect, useMemo, useContext, createContext } from 'react'
import { useAppDispatch, useAppSelector, shallowEqual } from 'hooks/reduxHooks'
import { fetchCalendarsByOccupancy } from 'actions/CalendarActions'
import generateOccupancyStringByRoom from 'lib/offer/generateOccupancyStringByRoom'
import { allRoomsOccupantsFitRoomCapacity } from 'lib/checkout/cartReservationUtils'
import { min, sortBy, sum } from 'lib/array/arrayUtils'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { EmptyObject } from 'lib/object/objectUtils'
import { FlightsClassTypes } from 'constants/flight'
import { useBundleFlightData } from './useBundleFlightData'
import type { DateRange, CalendarItemBundleData, FlightDataByDate } from './types'
import CurrencyContext from 'contexts/currencyContext'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import { OfferUnavailableReason } from 'lib/search/constants'
import { getCharterFareClassPolicy, resolveAllowedFareClass } from 'lib/charterFlights/charterOfferFareClassRestrictions'

export interface AllocatedRoomPackage {
  roomIndex: number
  room: App.Occupants
  package: App.HotelPackage
  hotelPrice: number
  hotelMemberPrice?: number
  hotelValue?: number
  availableRooms?: number
}

export interface OfferPricingData {
  // Cheapest date info
  cheapestDate: string | undefined
  cheapestDateRange: DateRange | undefined

  // Pricing for the effective date (selected or cheapest)
  bundleTotalPrice: number | undefined
  bundleMemberTotalPrice: number | undefined
  bundlePricePerPerson: number | undefined
  bundleMemberPricePerPerson: number | undefined
  hotelTotalPrice: number | undefined
  flightTotalPrice: number | undefined
  hotelValue: number | undefined
  discountPercent: number | undefined

  // Room allocation for display
  allocatedPackages: Array<AllocatedRoomPackage>
  roomBreakdown: Array<{ name: string; count: number; size?: string }>
  packageIds: Array<string> // Package IDs for each room (for SearchTileRoomBreakdown)

  // Guest info
  totalGuests: number

  // Loading/availability state
  isLoading: boolean
  hasAvailability: boolean
  unavailableReason?: string
}

export interface MaldivesOffersPricingContextValue {
  pricingByOfferId: Record<string, OfferPricingData>
  /** Aggregate pricing by date - lowest price across all offers for each date */
  calendarPricingByDate: Record<string, CalendarItemBundleData>
  isLoading: boolean
  hasSelectedDates: boolean
  selectedCheckIn: string | undefined
  selectedCheckOut: string | undefined
}

export const MaldivesOffersPricingContext = createContext<MaldivesOffersPricingContextValue>({
  pricingByOfferId: {},
  calendarPricingByDate: {},
  isLoading: true,
  hasSelectedDates: false,
  selectedCheckIn: undefined,
  selectedCheckOut: undefined,
})

export function useMaldivesOffersPricingContext() {
  return useContext(MaldivesOffersPricingContext)
}

type FlightDataByFareClassMap = Partial<Record<FlightsClassTypes, Record<string, FlightDataByDate>>>

function useFlightDataByFareClass(params: {
  rooms: Array<App.Occupants>
  dateRangesByMonth: Record<string, Array<DateRange>>
  searchFareClass: FlightsClassTypes
  currency: string
  charterBundleRoute?: App.CharterBundleRoute
  restrictedFareClassesNeeded: Set<FlightsClassTypes>
}): {
  flightDataByFareClass: FlightDataByFareClassMap
  isFlightLoading: boolean
} {
  const {
    rooms,
    dateRangesByMonth,
    searchFareClass,
    currency,
    charterBundleRoute,
    restrictedFareClassesNeeded,
  } = params

  const { flightDataByDate } = useBundleFlightData({
    rooms,
    dateRangesByMonth,
    fareClass: searchFareClass,
    currency,
    charterBundleRoute,
  })
  const shouldFetchPremium = restrictedFareClassesNeeded.has(FlightsClassTypes.PREMIUM)
  const shouldFetchBusiness = restrictedFareClassesNeeded.has(FlightsClassTypes.BUSINESS)
  const shouldFetchLuxRow = restrictedFareClassesNeeded.has(FlightsClassTypes.LUXROW)
  const { flightDataByDate: premiumFlightDataByDate } = useBundleFlightData({
    rooms,
    dateRangesByMonth,
    fareClass: FlightsClassTypes.PREMIUM,
    currency,
    charterBundleRoute,
    disabled: !shouldFetchPremium,
  })
  const { flightDataByDate: businessFlightDataByDate } = useBundleFlightData({
    rooms,
    dateRangesByMonth,
    fareClass: FlightsClassTypes.BUSINESS,
    currency,
    charterBundleRoute,
    disabled: !shouldFetchBusiness,
  })
  const { flightDataByDate: luxRowFlightDataByDate } = useBundleFlightData({
    rooms,
    dateRangesByMonth,
    fareClass: FlightsClassTypes.LUXROW,
    currency,
    charterBundleRoute,
    disabled: !shouldFetchLuxRow,
  })

  const isFlightLoading = Object.values(flightDataByDate).some(d => d.fetching) ||
    (shouldFetchPremium && Object.values(premiumFlightDataByDate).some(d => d.fetching)) ||
    (shouldFetchBusiness && Object.values(businessFlightDataByDate).some(d => d.fetching)) ||
    (shouldFetchLuxRow && Object.values(luxRowFlightDataByDate).some(d => d.fetching))
  const flightDataByFareClass = useMemo<FlightDataByFareClassMap>(() => ({
    [searchFareClass]: flightDataByDate,
    ...(shouldFetchPremium && searchFareClass !== FlightsClassTypes.PREMIUM
      ? { [FlightsClassTypes.PREMIUM]: premiumFlightDataByDate }
      : {}),
    ...(shouldFetchBusiness && searchFareClass !== FlightsClassTypes.BUSINESS
      ? { [FlightsClassTypes.BUSINESS]: businessFlightDataByDate }
      : {}),
    ...(shouldFetchLuxRow && searchFareClass !== FlightsClassTypes.LUXROW
      ? { [FlightsClassTypes.LUXROW]: luxRowFlightDataByDate }
      : {}),
  }), [
    searchFareClass,
    flightDataByDate,
    premiumFlightDataByDate,
    businessFlightDataByDate,
    luxRowFlightDataByDate,
    shouldFetchPremium,
    shouldFetchBusiness,
    shouldFetchLuxRow,
  ])

  return {
    flightDataByFareClass,
    isFlightLoading,
  }
}

function resolveFlightPriceForFareClass(
  flightData: {
    totalPrice?: number
    departingJourney?: App.JourneyV2
    returningJourney?: App.JourneyV2
  } | undefined,
  fareClass: FlightsClassTypes,
): number | undefined {
  if (!flightData) {
    return undefined
  }
  if (!flightData.departingJourney || !flightData.returningJourney) {
    return flightData.totalPrice
  }
  const departingPrice = getJourneyTotalFareForFareClass({
    journey: flightData.departingJourney,
    fareClass,
  })
  const returningPrice = getJourneyTotalFareForFareClass({
    journey: flightData.returningJourney,
    fareClass,
  })
  if (departingPrice === undefined || returningPrice === undefined) {
    return flightData.totalPrice
  }
  return departingPrice + returningPrice
}

interface UseMaldivesOffersPricingParams {
  offers: Array<App.HotelOffer>
  rooms: Array<App.Occupants>
  dateRangesByMonth: Record<string, Array<DateRange>>
  selectedFareClass?: FlightsClassTypes
  searchFareClass: FlightsClassTypes
  charterBundleRoute?: App.CharterBundleRoute
  selectedCheckIn?: string
  selectedCheckOut?: string
  nights: number
}

/**
 * Hook to calculate pricing for all offers on the landing page.
 * This fetches calendar data for all offers and computes optimal room allocation.
 */
export function useMaldivesOffersPricing(params: UseMaldivesOffersPricingParams): MaldivesOffersPricingContextValue {
  const {
    offers,
    rooms,
    dateRangesByMonth,
    selectedFareClass,
    searchFareClass,
    charterBundleRoute,
    selectedCheckIn,
    selectedCheckOut,
    nights,
  } = params

  const dispatch = useAppDispatch()
  const currencyContext = useContext(CurrencyContext)
  const currency: string = currencyContext || 'AUD'
  const totalPassengers = useMemo(() => sumUpOccupancies(rooms), [rooms])
  const seatEligiblePassengers = useMemo(
    () => sumUpOccupancies(rooms, new Set(['adults', 'children', 'teenagers'])),
    [rooms],
  )
  const fareClassPolicyByOfferId = useMemo(() => {
    const map = new Map<string, ReturnType<typeof getCharterFareClassPolicy>>()
    offers.forEach((offer) => {
      map.set(offer.id, getCharterFareClassPolicy({
        campaignTags: offer.campaignTags,
        occupancies: rooms,
      }))
    })
    return map
  }, [offers, rooms])
  const restrictedFareClassesNeeded = useMemo(() => {
    const needed = new Set<FlightsClassTypes>()
    if (selectedFareClass) {
      return needed
    }
    offers.forEach((offer) => {
      const policy = fareClassPolicyByOfferId.get(offer.id)
      if (!policy || policy.taggedFareClasses.length === 0) {
        return
      }
      const resolved = resolveAllowedFareClass({
        selectedFareClass,
        campaignTags: offer.campaignTags,
        occupancies: rooms,
      })
      if (resolved) {
        needed.add(resolved)
      }
    })
    return needed
  }, [fareClassPolicyByOfferId, offers, rooms, selectedFareClass])

  const offerPackagesMap = useMemo(() => {
    const map: Record<string, Array<App.HotelPackage>> = {}
    for (const offer of offers) {
      map[offer.id] = offer.packages.filter(pkg => pkg.duration === nights)
    }
    return map
  }, [offers, nights])

  useEffect(() => {
    if (rooms.length === 0 || offers.length === 0) return

    for (const offer of offers) {
      const packages = offerPackagesMap[offer.id]
      if (packages.length === 0) continue

      dispatch(fetchCalendarsByOccupancy(offer.id, {
        occupants: rooms,
        packages,
      }))
    }
  }, [dispatch, offers, rooms, offerPackagesMap])

  const calendarDataByOfferAndOccupancy = useAppSelector(state => {
    const result: Record<string, Record<string, Record<string, App.Calendar>>> = {}

    for (const offer of offers) {
      const packages = offerPackagesMap[offer.id]
      if (packages.length === 0) continue

      result[offer.id] = {}

      for (const room of rooms) {
        const occupancyKey = generateOccupancyStringByRoom(room)
        const occupancyCalendars = state.calendar.calendarsByOccupancy[occupancyKey]

        if (occupancyCalendars) {
          result[offer.id][occupancyKey] = {}
          for (const pkg of packages) {
            if (occupancyCalendars[pkg.uniqueKey]) {
              result[offer.id][occupancyKey][pkg.uniqueKey] = occupancyCalendars[pkg.uniqueKey]
            }
          }
        }
      }
    }

    return result
  }, shallowEqual)

  const isCalendarLoading = useAppSelector(state => {
    if (rooms.length === 0 || offers.length === 0) return false

    const result = offers.some(offer => {
      return rooms.some(room => {
        const occupancyKey = generateOccupancyStringByRoom(room)
        const packages = offerPackagesMap[offer.id]
        return packages.some(pkg => {
          const loadingFlag = state.calendar.calendarsByOccupancyLoading[occupancyKey]?.[pkg.uniqueKey]
          const hasData = !!state.calendar.calendarsByOccupancy[occupancyKey]?.[pkg.uniqueKey]
          // Treat "not yet requested / not yet present" as loading to avoid early false negatives.
          return loadingFlag || (!hasData && loadingFlag === undefined)
        })
      })
    })

    return result
  })

  const { flightDataByFareClass, isFlightLoading } = useFlightDataByFareClass({
    rooms,
    dateRangesByMonth,
    searchFareClass,
    currency,
    charterBundleRoute,
    restrictedFareClassesNeeded,
  })

  const { pricingByOfferId, calendarPricingByDate } = useMemo(() => {
    const offerResult: Record<string, OfferPricingData> = {}
    const allDates = Object.values(dateRangesByMonth).flat()

    // Initialize calendar aggregate for all dates
    const calendarResult: Record<string, CalendarItemBundleData> = {}
    for (const dateRange of allDates) {
      calendarResult[dateRange.checkIn] = {
        fetching: isCalendarLoading || isFlightLoading,
        available: false,
        totalPrice: 0,
        pricePerPerson: 0,
        offerCount: 0,
      }
    }

    for (const offer of offers) {
      const packages = offerPackagesMap[offer.id]
      const calendarDataByOccupancy = calendarDataByOfferAndOccupancy[offer.id]

      const fareClassPolicy = fareClassPolicyByOfferId.get(offer.id) ?? getCharterFareClassPolicy({
        campaignTags: offer.campaignTags,
        occupancies: rooms,
      })
      if (
        selectedFareClass &&
        fareClassPolicy.allowedFareClasses.length > 0 &&
        !fareClassPolicy.allowedFareClasses.includes(selectedFareClass)
      ) {
        offerResult[offer.id] = {
          ...createEmptyPricingData(false),
          unavailableReason: OfferUnavailableReason.FARE_CLASS_UNAVAILABLE,
        }
        continue
      }
      const effectiveFareClass = selectedFareClass ?? resolveAllowedFareClass({
        selectedFareClass,
        campaignTags: offer.campaignTags,
        occupancies: rooms,
      }) ?? searchFareClass

      if (packages.length === 0 || !calendarDataByOccupancy) {
        offerResult[offer.id] = createEmptyPricingData(false)
        continue
      }

      // Calculate pricing for all dates
      const pricingForAllDates: Array<{
        date: string
        dateRange: DateRange
        bundleTotalPrice: number
        bundleMemberTotalPrice?: number
        hotelPrice: number
        flightPrice: number
        hotelValue?: number
        allocatedPackages: Array<AllocatedRoomPackage>
        roomBreakdown: Array<{ name: string; count: number; size?: string }>
        packageIds: Array<string>
        roomsLeft?: number
        seatsLeft?: number
      }> = []

      for (const dateRange of allDates) {
        const { checkIn } = dateRange
        const classFlightData = flightDataByFareClass[effectiveFareClass] ??
          flightDataByFareClass[searchFareClass] ??
          EmptyObject as Record<string, FlightDataByDate>
        const flightData = classFlightData[checkIn]
        const flightPrice = resolveFlightPriceForFareClass(flightData, effectiveFareClass)

        if (!flightData?.totalPrice || !flightPrice) continue
        // If we have seats data and not enough seats for seat-eligible passengers, skip this date
        if (!hasSufficientSeats(flightData.seatsLeft, seatEligiblePassengers)) continue

        const allocation = allocatePackagesForDate({
          offer,
          rooms,
          durationPackages: packages,
          calendarDataByOccupancy,
          checkIn,
        })

        if (!allocation.isValid) continue

        const bundleTotalPrice = allocation.totalHotelPrice + flightPrice
        const bundleMemberTotalPrice = allocation.totalHotelMemberPrice
          ? allocation.totalHotelMemberPrice + flightPrice
          : undefined

        pricingForAllDates.push({
          date: checkIn,
          dateRange,
          bundleTotalPrice,
          bundleMemberTotalPrice,
          hotelPrice: allocation.totalHotelPrice,
          flightPrice: flightPrice,
          hotelValue: allocation.totalHotelValue,
          allocatedPackages: allocation.allocatedPackages,
          roomBreakdown: allocation.roomBreakdown,
          packageIds: allocation.allocatedPackages.map(p => p.package.id),
          roomsLeft: allocation.minRoomsLeft,
          seatsLeft: flightData.seatsLeft,
        })

        // Update calendar aggregate for this date
        const calendarEntry = calendarResult[checkIn]
        if (calendarEntry) {
          // Only count/mark available when seats are sufficient (if known)
          calendarEntry.offerCount += 1
          calendarEntry.available = true
          calendarEntry.fetching = false

          // Update with lowest price
          if (!calendarEntry.totalPrice || bundleTotalPrice < calendarEntry.totalPrice) {
            calendarEntry.totalPrice = bundleTotalPrice
            calendarEntry.pricePerPerson = totalPassengers > 0 ? bundleTotalPrice / totalPassengers : 0
            calendarEntry.memberTotalPrice = bundleMemberTotalPrice
            calendarEntry.memberPricePerPerson = bundleMemberTotalPrice && totalPassengers > 0
              ? bundleMemberTotalPrice / totalPassengers
              : undefined
            calendarEntry.hotelPrice = allocation.totalHotelPrice
            calendarEntry.hotelValue = allocation.totalHotelValue
            calendarEntry.flightPrice = flightPrice
            calendarEntry.roomsLeft = allocation.minRoomsLeft
            calendarEntry.seatsLeft = flightData.seatsLeft
          }
        }
      }

      if (pricingForAllDates.length === 0) {
        // Determine unavailability reason for selected date if applicable (e.g., flight class seats insufficient)
        let unavailableReason: string | undefined
        if (selectedCheckIn) {
          const classFlightData = flightDataByFareClass[effectiveFareClass] ??
            flightDataByFareClass[searchFareClass] ??
            EmptyObject as Record<string, FlightDataByDate>
          const selectedFlightData = classFlightData[selectedCheckIn]
          const seatsLeft = selectedFlightData?.seatsLeft
          if (!hasSufficientSeats(seatsLeft, seatEligiblePassengers)) {
            unavailableReason = OfferUnavailableReason.FLIGHT_CLASS_UNAVAILABLE
          }
        }
        offerResult[offer.id] = {
          ...createEmptyPricingData(false),
          unavailableReason,
        }
        continue
      }

      // Find cheapest date
      const cheapestEntry = pricingForAllDates.reduce((cheapest, current) =>
        current.bundleTotalPrice < cheapest.bundleTotalPrice ? current : cheapest,
      )

      // Get pricing for effective date (selected or cheapest)
      const effectiveDate = selectedCheckIn ?? cheapestEntry.date
      const effectiveEntry = pricingForAllDates.find(e => e.date === effectiveDate)

      // If user selected a date that has no availability for this offer, show as unavailable
      if (!effectiveEntry) {
        // Selected date has no availability for this offer
        // Determine if due to charter flight class seats
        let unavailableReason: string | undefined
        if (selectedCheckIn) {
          const classFlightData = flightDataByFareClass[effectiveFareClass] ??
            flightDataByFareClass[searchFareClass] ??
            EmptyObject as Record<string, FlightDataByDate>
          const selectedFlightData = classFlightData[selectedCheckIn]
          const seatsLeft = selectedFlightData?.seatsLeft
          if (!hasSufficientSeats(seatsLeft, seatEligiblePassengers)) {
            unavailableReason = OfferUnavailableReason.FLIGHT_CLASS_UNAVAILABLE
          }
        }
        offerResult[offer.id] = {
          ...createEmptyPricingData(false),
          unavailableReason,
        }
        continue
      }

      const discountPercent = getValueOffPercent(
        (effectiveEntry.hotelValue ?? effectiveEntry.hotelPrice) + effectiveEntry.flightPrice,
        effectiveEntry.bundleTotalPrice,
      )

      offerResult[offer.id] = {
        cheapestDate: cheapestEntry.date,
        cheapestDateRange: cheapestEntry.dateRange,
        bundleTotalPrice: effectiveEntry.bundleTotalPrice,
        bundleMemberTotalPrice: effectiveEntry.bundleMemberTotalPrice,
        bundlePricePerPerson: totalPassengers > 0 ? effectiveEntry.bundleTotalPrice / totalPassengers : undefined,
        bundleMemberPricePerPerson: effectiveEntry.bundleMemberTotalPrice && totalPassengers > 0
          ? effectiveEntry.bundleMemberTotalPrice / totalPassengers
          : undefined,
        hotelTotalPrice: effectiveEntry.hotelPrice,
        flightTotalPrice: effectiveEntry.flightPrice,
        hotelValue: effectiveEntry.hotelValue,
        discountPercent,
        allocatedPackages: effectiveEntry.allocatedPackages,
        roomBreakdown: effectiveEntry.roomBreakdown,
        packageIds: effectiveEntry.packageIds,
        totalGuests: totalPassengers,
        isLoading: false,
        hasAvailability: true,
        unavailableReason: undefined,
      }
    }

    // Ensure dates with no available offers are marked as not fetching
    for (const checkIn of Object.keys(calendarResult)) {
      if (calendarResult[checkIn].offerCount === 0) {
        calendarResult[checkIn].fetching = false
      }
    }

    return { pricingByOfferId: offerResult, calendarPricingByDate: calendarResult }
  }, [
    offers,
    offerPackagesMap,
    calendarDataByOfferAndOccupancy,
    dateRangesByMonth,
    flightDataByFareClass,
    fareClassPolicyByOfferId,
    rooms,
    totalPassengers,
    seatEligiblePassengers,
    selectedCheckIn,
    isCalendarLoading,
    isFlightLoading,
    selectedFareClass,
    searchFareClass,
  ])

  const isLoading = isCalendarLoading || isFlightLoading
  const hasSelectedDates = !!(selectedCheckIn && selectedCheckOut)

  return {
    pricingByOfferId,
    calendarPricingByDate,
    isLoading,
    hasSelectedDates,
    selectedCheckIn,
    selectedCheckOut,
  }
}

function createEmptyPricingData(isLoading: boolean): OfferPricingData {
  return {
    cheapestDate: undefined,
    cheapestDateRange: undefined,
    bundleTotalPrice: undefined,
    bundleMemberTotalPrice: undefined,
    bundlePricePerPerson: undefined,
    bundleMemberPricePerPerson: undefined,
    hotelTotalPrice: undefined,
    flightTotalPrice: undefined,
    hotelValue: undefined,
    discountPercent: undefined,
    allocatedPackages: [],
    roomBreakdown: [],
    packageIds: [],
    totalGuests: 0,
    isLoading,
    hasAvailability: false,
  }
}

/**
 * Allocate packages to rooms for a specific date.
 */
function allocatePackagesForDate(params: {
  offer: App.HotelOffer
  rooms: Array<App.Occupants>
  durationPackages: Array<App.HotelPackage>
  calendarDataByOccupancy: Record<string, Record<string, App.Calendar>>
  checkIn: string
}): {
  allocatedPackages: Array<AllocatedRoomPackage>
  totalHotelPrice: number
  totalHotelMemberPrice?: number
  totalHotelValue?: number
  roomBreakdown: Array<{ name: string; count: number; size?: string }>
  isValid: boolean
  minRoomsLeft?: number
} {
  const { offer, rooms, durationPackages, calendarDataByOccupancy, checkIn } = params

  const emptyResult = {
    allocatedPackages: [] as Array<AllocatedRoomPackage>,
    totalHotelPrice: 0,
    totalHotelMemberPrice: undefined as number | undefined,
    totalHotelValue: undefined as number | undefined,
    roomBreakdown: [] as Array<{ name: string; count: number; size?: string }>,
    isValid: false,
  }

  if (rooms.length === 0 || durationPackages.length === 0) {
    return emptyResult
  }

  type PackageWithDatePricing = {
    package: App.HotelPackage
    hotelPrice: number
    hotelMemberPrice?: number
    hotelValue?: number
    availableRooms?: number
  }

  // For each room, find valid packages with pricing
  const validPackagesByRoom: Array<{
    roomIndex: number
    room: App.Occupants
    validPackages: Array<PackageWithDatePricing>
  }> = rooms.map((room, index) => {
    const occupancyKey = generateOccupancyStringByRoom(room)
    const occupancyCalendars = calendarDataByOccupancy[occupancyKey]

    const validPackages: Array<PackageWithDatePricing> = []

    for (const pkg of durationPackages) {
      const capacities = pkg.roomRate?.capacities ?? []
      if (!allRoomsOccupantsFitRoomCapacity(room, capacities, offer)) {
        continue
      }

      const calendar = occupancyCalendars?.[pkg.uniqueKey]
      if (!calendar) {
        continue
      }

      let dayData: App.CalendarDay | undefined
      for (const month of calendar.months) {
        dayData = month.days.find(d => d.checkIn === checkIn)
        if (dayData) break
      }

      if (!dayData?.hotelAvailable) {
        continue
      }

      validPackages.push({
        package: pkg,
        hotelPrice: dayData.hotelTotal,
        hotelMemberPrice: dayData.hotelMemberTotal || undefined,
        hotelValue: dayData.hotelValue,
        availableRooms: dayData.availableRooms,
      })
    }

    return { roomIndex: index, room, validPackages }
  })

  // Sort rooms by occupancy descending
  const getTotalOccupancy = (room: App.Occupants) =>
    room.adults + (room.teenagers ?? 0) + (room.children ?? 0) + (room.infants ?? 0)

  const sortedRooms = sortBy(
    validPackagesByRoom,
    item => getTotalOccupancy(item.room),
    'desc',
  )

  // Allocate packages tracking inventory
  const allocatedPackages: Array<AllocatedRoomPackage> = []
  const usedInventory: Record<string, number> = {}

  for (const roomData of sortedRooms) {
    const availablePackages = roomData.validPackages.filter(pkg => {
      const used = usedInventory[pkg.package.uniqueKey] ?? 0
      const available = pkg.availableRooms ?? Infinity
      return available - used > 0
    })

    if (availablePackages.length === 0) {
      return emptyResult
    }

    const cheapest = min(availablePackages, pkg => pkg.hotelPrice)
    if (!cheapest) {
      return emptyResult
    }

    usedInventory[cheapest.package.uniqueKey] = (usedInventory[cheapest.package.uniqueKey] ?? 0) + 1

    allocatedPackages.push({
      roomIndex: roomData.roomIndex,
      room: roomData.room,
      package: cheapest.package,
      hotelPrice: cheapest.hotelPrice,
      hotelMemberPrice: cheapest.hotelMemberPrice,
      hotelValue: cheapest.hotelValue,
      availableRooms: cheapest.availableRooms,
    })
  }

  allocatedPackages.sort((a, b) => a.roomIndex - b.roomIndex)

  const totalHotelPrice = sum(allocatedPackages, pkg => pkg.hotelPrice)
  const allHaveMemberPrice = allocatedPackages.every(pkg => pkg.hotelMemberPrice !== undefined)
  const totalHotelMemberPrice = allHaveMemberPrice
    ? sum(allocatedPackages, pkg => pkg.hotelMemberPrice ?? 0)
    : undefined
  const allHaveValue = allocatedPackages.every(pkg => pkg.hotelValue !== undefined)
  const totalHotelValue = allHaveValue
    ? sum(allocatedPackages, pkg => pkg.hotelValue ?? 0)
    : undefined

  // Build room breakdown
  const packageCounts: Record<string, { name: string; count: number; size?: string }> = {}
  for (const allocated of allocatedPackages) {
    const roomType = allocated.package.roomType
    if (roomType) {
      const key = roomType.id
      if (!packageCounts[key]) {
        packageCounts[key] = {
          name: roomType.name,
          count: 0,
          size: roomType.sizeSqm ? `${roomType.sizeSqm}m²` : undefined,
        }
      }
      packageCounts[key].count += 1
    }
  }

  const roomBreakdown = Object.values(packageCounts).map(item => ({
    name: `${item.count}x ${item.name}`,
    count: item.count,
    size: item.size,
  }))

  // Calculate minimum rooms left across all allocated packages
  const minRoomsLeft = allocatedPackages.reduce((min, pkg) => {
    const available = pkg.availableRooms
    if (available === undefined) return min
    return min === undefined ? available : Math.min(min, available)
  }, undefined as number | undefined)

  return {
    allocatedPackages,
    totalHotelPrice,
    totalHotelMemberPrice,
    totalHotelValue,
    roomBreakdown,
    isValid: allocatedPackages.length === rooms.length,
    minRoomsLeft,
  }
}
